import type { Identifier, RaRecord } from 'react-admin';

import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export const ADMIN_AI_SCORE_FEEDBACK_RESOURCE = 'ai-score-feedback' as const;

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type AdminAiScoreFeedbackRecord = RaRecord & {
    id: string;
    aiScoreFeedbackId: string;
    [key: string]: unknown;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getRequestUrl(path = '', query?: Record<string, string>): URL {
    const { baseUrl, apiVersion } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const requestPath = trimSlashes(path);
    const url = new URL(
        requestPath
            ? `${apiRoot}/AdminAiScoreFeedback/${requestPath}`
            : `${apiRoot}/AdminAiScoreFeedback`,
    );

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    return url;
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'AI score feedback request failed'));
    }
    return envelope.data !== undefined ? envelope.data : (payload as T);
}

function normalizeRecord(item: unknown, fallbackId?: Identifier): AdminAiScoreFeedbackRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.aiScoreFeedbackId ?? fallbackId;
    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('AI score feedback item is missing aiScoreFeedbackId');
    }

    return {
        ...record,
        id: String(rawId),
        aiScoreFeedbackId: String(record.aiScoreFeedbackId ?? rawId),
    };
}

function asObject(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function getListRecords(payload: unknown): unknown[] {
    const unwrapped = unwrapAdminApiPayload(payload as unknown[] | AdminApiEnvelope<unknown[]>);
    if (Array.isArray(unwrapped)) return unwrapped;
    if (unwrapped && typeof unwrapped === 'object') {
        const candidate = unwrapped as Record<string, unknown>;
        if (Array.isArray(candidate.items)) return candidate.items;
        if (Array.isArray(candidate.data)) return candidate.data;
    }
    return [];
}

function getListTotal(payload: unknown, fallback: number): number {
    const unwrapped = unwrapAdminApiPayload(
        payload as Record<string, unknown> | AdminApiEnvelope<Record<string, unknown>>,
    );
    if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
        if (typeof unwrapped.totalCount === 'number') return unwrapped.totalCount;
        if (typeof unwrapped.total === 'number') return unwrapped.total;
        if (typeof unwrapped.count === 'number') return unwrapped.count;
    }
    return fallback;
}

function toQueryValue(value: unknown): string {
    return value === undefined || value === null ? '' : String(value).trim();
}

async function requestAdminAiScoreFeedback<T>(
    path = '',
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { adminToken } = getAdminApiConfig();
    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(getRequestUrl(path, query).toString(), {
        ...init,
        headers,
    });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }
    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'AI score feedback request failed'));
        }
    }
    return payload as T;
}

export function isAdminAiScoreFeedbackResource(
    resource: string,
): resource is typeof ADMIN_AI_SCORE_FEEDBACK_RESOURCE {
    return resource === ADMIN_AI_SCORE_FEEDBACK_RESOURCE;
}

export async function getAdminAiScoreFeedbackList(params: any) {
    const filter = (params.filter ?? {}) as Record<string, unknown>;
    const { page = 1, perPage = 10 } = params.pagination ?? {};
    const payload = await requestAdminAiScoreFeedback<unknown>(
        '',
        { method: 'GET' },
        {
            page: String(page),
            pageSize: String(perPage),
            search: toQueryValue(filter.search ?? filter.q ?? ''),
            status: toQueryValue(filter.status ?? ''),
        },
    );
    const records = getListRecords(payload).map((item) => normalizeRecord(item));
    return {
        data: records,
        total: getListTotal(payload, records.length),
    };
}

export async function getAdminAiScoreFeedbackOne(params: any) {
    const payload = unwrapAdminApiPayload(
        await requestAdminAiScoreFeedback<unknown | AdminApiEnvelope<unknown>>(String(params.id), {
            method: 'GET',
        }),
    );
    return { data: normalizeRecord(payload, params.id) };
}

export async function getManyAdminAiScoreFeedback(params: any) {
    const data = await Promise.all(
        params.ids.map(async (id: Identifier) => {
            const payload = unwrapAdminApiPayload(
                await requestAdminAiScoreFeedback<unknown | AdminApiEnvelope<unknown>>(String(id), {
                    method: 'GET',
                }),
            );
            return normalizeRecord(payload, id);
        }),
    );
    return { data };
}

export async function updateAdminAiScoreFeedback(params: any) {
    const payload = unwrapAdminApiPayload(
        await requestAdminAiScoreFeedback<unknown | AdminApiEnvelope<unknown>>('', {
            method: 'POST',
            body: JSON.stringify({
                aiScoreFeedbackId: params.data?.aiScoreFeedbackId ?? params.id,
                status: params.data?.status ?? params.previousData?.status ?? '',
                sourceLanguageCode:
                    params.data?.sourceLanguageCode ?? params.previousData?.sourceLanguageCode,
                transcriptionText:
                    params.data?.transcriptionText ?? params.previousData?.transcriptionText,
                translatedMessage:
                    params.data?.translatedMessage ?? params.previousData?.translatedMessage,
                translationProvider:
                    params.data?.translationProvider ?? params.previousData?.translationProvider,
            }),
        }),
    );
    const nextRecord = {
        ...asObject(params.previousData),
        ...asObject(payload),
        aiScoreFeedbackId: params.data?.aiScoreFeedbackId ?? params.id,
        status: params.data?.status ?? params.previousData?.status ?? '',
        sourceLanguageCode:
            params.data?.sourceLanguageCode ?? params.previousData?.sourceLanguageCode,
        transcriptionText:
            params.data?.transcriptionText ?? params.previousData?.transcriptionText,
        translatedMessage:
            params.data?.translatedMessage ?? params.previousData?.translatedMessage,
        translationProvider:
            params.data?.translationProvider ?? params.previousData?.translationProvider,
    };
    return { data: normalizeRecord(nextRecord, params.id) };
}

export async function saveBrowserTranslation(
    record: AdminAiScoreFeedbackRecord,
    translatedMessage: string,
    sourceLanguageCode: string,
) {
    return updateAdminAiScoreFeedback({
        id: record.aiScoreFeedbackId,
        previousData: record,
        data: {
            ...record,
            translatedMessage,
            sourceLanguageCode,
            translationProvider: 'chrome_builtin_translator',
        },
    });
}

export async function deleteAdminAiScoreFeedback(params: any) {
    const existing = params.previousData
        ? normalizeRecord(params.previousData, params.id)
        : (await getAdminAiScoreFeedbackOne(params)).data;
    await requestAdminAiScoreFeedback(String(params.id), { method: 'DELETE' });
    return { data: existing };
}

export async function fetchAdminAiScoreFeedbackAudio(aiScoreFeedbackId: Identifier): Promise<Blob> {
    const { adminToken } = getAdminApiConfig();
    const headers = new Headers({ Accept: 'audio/*,application/octet-stream' });
    if (adminToken) headers.set('Authorization', `Bearer ${adminToken}`);
    const response = await fetch(getRequestUrl(`${aiScoreFeedbackId}/audio`).toString(), { headers });
    if (!response.ok) {
        const contentType = response.headers.get('content-type') ?? '';
        const payload = contentType.includes('application/json') ? await response.json() : await response.text();
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }
    return response.blob();
}
